<script setup lang="ts">
import { ref, onMounted, onUnmounted, inject } from 'vue'
import {
  getStoredTerminal,
  setStoredTerminal,
} from './../utilities/terminal-storage-helper'
import { AppSettingsKey } from './../types/keys'
import type {
  AppSettings,
  TravelInfoSettings,
  TravelInfoTerminals,
} from './../types'
import { Terminal } from './../types/enums'
import TravelInfoService from './../services/travel-info-service'
import { handleLoadingState } from './../utilities/async-helper'
import TravelInfo from './travel-info/TravelInfo.vue'
import StatusAlert from './travel-info/StatusAlert.vue'
import DeparturesBoard from './departures/DeparturesBoard.vue'
import LoadingWrapper from './shared/LoadingWrapper.vue'

let travelInfoRefreshInterval: number
let travelInfoSettings = ref<TravelInfoSettings>()
let travelInfoTerminals = ref<TravelInfoTerminals | null>()

const appSettings = inject(AppSettingsKey) as AppSettings
const travelInfoService = new TravelInfoService(appSettings.apiBaseUrl)
const activeTerminal = ref<Terminal>(Terminal.Uk)
const errorState = ref<boolean>(false)
const loadingState = ref<boolean>(true)
const mounted = ref<boolean>(false)

async function fetchTravelInfo(): Promise<void> {
  await handleLoadingState(loadingState, errorState, async () => {
    const travelInfoResponse = await travelInfoService.GetTravelInfo()
    travelInfoSettings.value = travelInfoResponse.settings
    travelInfoTerminals.value = travelInfoResponse.terminals
    errorState.value = false
  })
}

function setActiveTerminal(terminal: Terminal): void {
  activeTerminal.value = terminal
  setStoredTerminal(terminal)
}

onMounted(async () => {
  await fetchTravelInfo()
  mounted.value = true
  activeTerminal.value = getStoredTerminal(
    appSettings?.defaultTerminal || Terminal.Uk
  )

  // Refresh travel info
  travelInfoRefreshInterval = window.setInterval(async () => {
    await fetchTravelInfo()
  }, (travelInfoSettings.value?.refreshRateSeconds || 30) * 1000)
})

onUnmounted(() => {
  clearInterval(travelInfoRefreshInterval)
})
</script>

<template>
  <div class="tab-select" data-tabs="">
    <div class="tab-select__list-cont">
      <div class="tab-select__list" role="tablist">
        <button
          class="tab-select__link"
          role="tab"
          :data-active="activeTerminal == Terminal.Uk"
          @click="setActiveTerminal(Terminal.Uk)"
        >
          {{ appSettings.terminalNameFolkestone }}
          <span class="tab-select__culture">
            {{ appSettings.resourceStrings['TravelUpdates.Labels.UK'] }}
          </span>
        </button>
        <button
          class="tab-select__link"
          role="tab"
          :data-active="activeTerminal == Terminal.Fr"
          @click="setActiveTerminal(Terminal.Fr)"
        >
          {{ appSettings.terminalNameCalais }}
          <span class="tab-select__culture">
            {{ appSettings.resourceStrings['TravelUpdates.Labels.FR'] }}
          </span>
        </button>
      </div>
    </div>
    <div v-if="errorState && !travelInfoTerminals" class="p-b-x10">
      <div class="context-err">
        <svg class="context-err__icon">
          <use xlink:href="#svg-warning"></use>
        </svg>
        {{
          appSettings.resourceStrings[
            'TravelUpdates.TravelInfo.ApiErrorMessage'
          ]
        }}
      </div>
    </div>
    <div v-show="activeTerminal == Terminal.Uk">
      <LoadingWrapper :loading="!mounted">
        <StatusAlert
          v-if="travelInfoSettings?.statusAlerts?.isVisible"
          :status-alert="travelInfoSettings.statusAlerts.folkestone"
        />
        <TravelInfo
          v-if="
            travelInfoTerminals && travelInfoSettings?.latestInfo?.isVisible
          "
          :settings="travelInfoSettings"
          :terminal-data="travelInfoTerminals.folkestone"
        />
      </LoadingWrapper>
      <DeparturesBoard :terminal="Terminal.Uk" />
    </div>
    <div v-show="activeTerminal == Terminal.Fr">
      <LoadingWrapper :loading="!mounted">
        <StatusAlert
          v-if="travelInfoSettings?.statusAlerts?.isVisible"
          :status-alert="travelInfoSettings.statusAlerts.calais"
        />
        <TravelInfo
          v-if="
            travelInfoTerminals && travelInfoSettings?.latestInfo?.isVisible
          "
          :settings="travelInfoSettings"
          :terminal-data="travelInfoTerminals.calais"
        />
      </LoadingWrapper>
      <DeparturesBoard :terminal="Terminal.Fr" />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
